import PropTypes from 'prop-types';
import './DownloadProgressCircle.css';

/**
 * Circular progress indicator for file downloads
 * @param {number} progress - Progress percentage (0-100), null for indeterminate
 * @param {number} size - Circle diameter in pixels
 * @param {boolean} showPercent - Whether to show percentage text
 */
const DownloadProgressCircle = ({ progress, size = 48, showPercent = true }) => {
  const strokeWidth = 3;
  const radius = (size - strokeWidth * 2) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;
  
  // Calculate stroke offset for progress
  const strokeDashoffset = progress !== null
    ? circumference - (progress / 100) * circumference
    : 0;

  // Determine if we should show indeterminate spinner
  const isIndeterminate = progress === null;

  return (
    <div 
      className="download-progress-circle" 
      style={{ width: size, height: size }}
    >
      <svg 
        width={size} 
        height={size} 
        style={{ transform: 'rotate(-90deg)' }}
        className={isIndeterminate ? 'indeterminate' : ''}
      >
        {/* Background circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          stroke="rgba(255, 255, 255, 0.3)"
          strokeWidth={strokeWidth}
          fill="transparent"
        />
        
        {/* Progress circle */}
        {!isIndeterminate && (
          <circle
            cx={center}
            cy={center}
            r={radius}
            stroke="#007AFF"
            strokeWidth={strokeWidth}
            fill="transparent"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            style={{ 
              transition: 'stroke-dashoffset 0.3s ease',
            }}
          />
        )}
        
        {/* Indeterminate spinner */}
        {isIndeterminate && (
          <circle
            cx={center}
            cy={center}
            r={radius}
            stroke="#007AFF"
            strokeWidth={strokeWidth}
            fill="transparent"
            strokeLinecap="round"
            strokeDasharray={`${circumference * 0.25} ${circumference * 0.75}`}
            style={{
              animation: 'spin 1s linear infinite',
            }}
          />
        )}
      </svg>
      
      {/* Percentage text (only for determinate progress) */}
      {showPercent && !isIndeterminate && (
        <div className="progress-percent">
          {Math.round(progress)}%
        </div>
      )}
      
      {/* Loading indicator for indeterminate */}
      {showPercent && isIndeterminate && (
        <div className="progress-loading">
          <i className="fa fa-spinner fa-spin" />
        </div>
      )}
    </div>
  );
};

DownloadProgressCircle.propTypes = {
  progress: PropTypes.number, // number (0-100) or null for indeterminate
  size: PropTypes.number,
  showPercent: PropTypes.bool,
};

export default DownloadProgressCircle;
